import type React from 'react'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { type Clip, isVoiceClip } from '@/features/clips/domain/models/clip.ts'
import { deleteClip } from '@/features/clips/domain/use-cases/delete-clip.ts'
import { recordChange } from '@/features/clips/domain/use-cases/record-change.ts'
import { useClickOutside } from '@/hooks/useClickOutside.tsx'
import useMediaStore from '../../../../shared/store/media.store.tsx'
import useTracksStore, {
  TrackType,
  trackSelector,
} from '../../../../shared/store/tracks.store.tsx'
import { updateTrackVolume } from '../../../../shared/store/use-cases/update-track-volume.ts'
import useUiStore from '../../../../shared/store/ui.store'
import { downloadClip } from '@/utils/download-clip.ts'
import AudioWave from '../../../common/AudioWave.tsx'
import Icon from '../../../common/Icon.tsx'
import GainSlider from '../../GainSlider/GainSlider.tsx'
import VolumeLayer from './VolumeLayer.tsx'
import { selectClip } from '@/features/clips/domain/use-cases/select-clip.ts'
import { useTracking } from '@/providers/TrackingProvider.tsx'

interface ClipContainerProps {
  clip: Clip
}

const minPlayableLength = 0.25

const WaveColors = {
  [TrackType.Voices]: '#10B981',
  [TrackType.MediaFiles]: '#F57149',
  [TrackType.SoundDesigns]: '#3B82F6',
  [TrackType.SoundEffects]: '#8B5CF6',
}

const ClipContainer: React.FC<ClipContainerProps> = ({ clip }) => {
  const { trackEvent } = useTracking()
  if (clip.startAt === undefined) return null
  const trackType = clip.type

  const {
    pixelsPerSecond,
    selectedClip,
    setIsSidebarOpen,
    openSpeechDialogForClip,
  } = useUiStore(
    useShallow((state) => ({
      pixelsPerSecond: state.pixelsPerSecond,
      selectedClip: state.selectedClip,
      setIsSidebarOpen: state.setIsSidebarOpen,
      openSpeechDialogForClip: state.openSpeechDialogForClip,
    }))
  )

  const {
    updateClip,
    getAvailablePosition,
    draggedClip,
    getMinStartOffset,
    getMinEndOffset,
  } = useTracksStore(
    useShallow((state) => ({
      updateClip: state.updateClip,
      getAvailablePosition: state.getAvailablePosition,
      draggedClip: state.draggedClip,
      getMinStartOffset: state.getMinStartOffset,
      getMinEndOffset: state.getMinEndOffset,
    }))
  )

  const containerRef = useRef<HTMLDivElement>(null)
  const audioBuffer = useMediaStore((state) => state.media[clip.mediaId])
  const width =
    (clip.duration - clip.startOffset + clip.endOffset) * pixelsPerSecond

  const leftPos = (clip.startAt + clip.startOffset) * pixelsPerSecond
  const isSelected = selectedClip?.id === clip.id

  const clickStartTime = useRef<number | null>(null)
  const [isResizingLeft, setIsResizingLeft] = useState(false)
  const [isResizingRight, setIsResizingRight] = useState(false)
  const [initialMouseX, setInitialMouseX] = useState(0)
  const [initialClip, setInitialClip] = useState(clip)
  const [isDragging, setIsDragging] = useState(false)
  const [isMouseDown, setIsMouseDown] = useState(false)
  const [isGainControlOpen, setIsGainControlOpen] = useState(false)
  const trackVolume = useTracksStore(
    (state) => trackSelector(clip.trackId)(state).volume
  )

  useClickOutside({
    itemRef: containerRef,
    triggerRef: containerRef,
    isOpen: isSelected,
    setIsOpen: (isOpen) => {
      if (!isOpen && !isVoiceClip(clip)) {
        setIsGainControlOpen(false)
        selectClip(undefined)
      }
    },
  })

  useEffect(() => {
    if (!isSelected) {
      setIsGainControlOpen(false)
    }
  }, [isSelected])

  const toggleGainControl = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    setIsGainControlOpen((open) => !open)
  }, [])

  const handleGainChange = useCallback(
    (gainDb: number) => {
      void updateTrackVolume(clip.trackId, gainDb)
    },
    [clip.trackId]
  )

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const deltaX = e.clientX - initialMouseX
      const deltaTime = deltaX / pixelsPerSecond

      if (initialClip.startAt === undefined) return

      if (isResizingLeft) {
        let newStartOffset = initialClip.startOffset + deltaTime
        if (newStartOffset < 0) newStartOffset = 0

        if (initialClip.startAt + newStartOffset < 0) {
          // so it doesn't go negative
          newStartOffset = -initialClip.startAt
        }

        const maxStartOffset =
          initialClip.duration + initialClip.endOffset - minPlayableLength
        if (newStartOffset > maxStartOffset) newStartOffset = maxStartOffset

        const minStartOffset = getMinStartOffset(clip)
        updateClip(clip.id, {
          startOffset:
            newStartOffset < minStartOffset ? minStartOffset : newStartOffset,
        })
        return
      }

      if (isResizingRight) {
        let newEndOffset = initialClip.endOffset + deltaTime
        if (newEndOffset > 0) newEndOffset = 0
        const maxEndOffset = -(
          initialClip.duration -
          initialClip.startOffset -
          minPlayableLength
        )
        if (newEndOffset < maxEndOffset) newEndOffset = maxEndOffset

        const minEndOffset = getMinEndOffset(clip)
        updateClip(clip.id, {
          endOffset: newEndOffset > minEndOffset ? minEndOffset : newEndOffset,
        })
        return
      }

      // if the clip is being dragged
      if (isMouseDown) {
        setIsDragging(true)

        const newPosition = Math.max(
          0 - initialClip.startOffset,
          initialClip.startAt + deltaTime
        )

        const newStartAt = getAvailablePosition(
          {
            ...clip,
            startAt: newPosition,
          },
          8 / pixelsPerSecond,
          clip.trackId
        )

        updateClip(clip.id, {
          startAt: newStartAt ?? newPosition,
          isOverlapping: newStartAt === undefined,
        })
      }
    },
    [
      isResizingLeft,
      isResizingRight,
      initialMouseX,
      initialClip,
      pixelsPerSecond,
      clip,
    ]
  )

  const handleMouseUp = useCallback(
    (event: MouseEvent) => {
      let isSelecting = true
      if ((event.target as HTMLElement)?.closest('[data-ignore-click]')) {
        isSelecting = false
      }

      const isContextMenuOpen = event.button === 2

      if (clickStartTime.current) {
        const elapsedTime = Date.now() - clickStartTime.current
        isSelecting &&= elapsedTime < 200

        if (isSelecting && !isDragging && !isContextMenuOpen) {
          if (isVoiceClip(clip)) {
            openSpeechDialogForClip(clip)
          }
          selectClip(clip)
        } else {
          setClipCorrectedPosition(clip)
          recordChange()
        }

        clickStartTime.current = null
      } else {
        setClipCorrectedPosition(clip)
        recordChange()
      }

      setIsResizingLeft(false)
      setIsResizingRight(false)
      setIsDragging(false)
      setIsMouseDown(false)
    },
    [isDragging, clip, setIsSidebarOpen, openSpeechDialogForClip]
  )

  const handleMouseDownLeft = useCallback(
    (e: React.MouseEvent) => {
      setIsResizingLeft(true)
      setIsMouseDown(false)
      setInitialMouseX(e.clientX)
      setInitialClip(clip)
    },
    [clip]
  )

  const handleMouseDownRight = useCallback(
    (e: React.MouseEvent) => {
      setIsResizingRight(true)
      setIsMouseDown(false)
      setInitialMouseX(e.clientX)
      setInitialClip(clip)
    },
    [clip]
  )

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      clickStartTime.current = Date.now()
      updateClip(clip.id, { lastPosition: clip.startAt })
      setIsMouseDown(true)
      setIsDragging(false)
      setInitialMouseX(e.clientX)
      setInitialClip(clip)
    },
    [clip]
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const selectedClip = useUiStore.getState().selectedClip
      if (
        (event.key === 'Backspace' || event.key === 'Delete') &&
        selectedClip?.id === clip.id
      ) {
        deleteClip(clip)
      }
    },
    [clip]
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)

    if (isResizingLeft || isResizingRight || isMouseDown) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [
    isResizingLeft,
    isResizingRight,
    isMouseDown,
    initialMouseX,
    initialClip,
    pixelsPerSecond,
    updateClip,
    clip,
  ])

  const onDownloadClip = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()

      if (!audioBuffer) return
      void downloadClip(clip, trackEvent)
    },
    [clip, audioBuffer, trackEvent]
  )

  const setClipCorrectedPosition = (clip: Clip) => {
    updateClip(clip.id, {
      startAt: clip.isOverlapping ? clip.lastPosition : clip.startAt,
      lastPosition: undefined,
      isOverlapping: false,
    })
  }

  const isMovingClip = isDragging || draggedClip?.id === clip.id

  return (
    <div
      ref={containerRef}
      className={`clip clip--${trackType} ${isSelected ? 'selected' : ''} ${
        audioBuffer ? '' : 'animate-[pulse_1.4s_ease-in-out_infinite]'
      } group absolute top-0 my-1 h-[126px] content-center rounded border`}
      style={{
        width,
        left: leftPos,
        ...(isMovingClip
          ? clip.isOverlapping
            ? { cursor: 'not-allowed' }
            : { cursor: 'grabbing' }
          : { cursor: 'default' }),
        opacity: clip.isOverlapping ? 0.5 : 1,
      }}
      onMouseDown={handleMouseDown}
    >
      {audioBuffer && (
        <div className="relative -z-0 h-20 overflow-hidden">
          <div
            className="absolute top-0"
            style={{
              width,
              left: clip.startOffset ? -clip.startOffset * pixelsPerSecond : 0,
            }}
          >
            <AudioWave
              audioBuffer={audioBuffer}
              startOffset={clip.startOffset * pixelsPerSecond}
              endOffset={clip.endOffset * pixelsPerSecond}
              width={audioBuffer.duration * pixelsPerSecond}
              height={80}
              color={isSelected ? '#F59E0B' : WaveColors[trackType]}
              spacing={0.1}
              lineWidth={1}
            />
          </div>
          {isGainControlOpen && (
            <div
              className="absolute inset-x-0 bottom-0 z-20 border-t border-zinc-600 bg-zinc-900/95 px-2 py-1.5"
              data-ignore-click
              onMouseDown={(e) => e.stopPropagation()}
            >
              <GainSlider value={trackVolume} onChange={handleGainChange} />
            </div>
          )}
        </div>
      )}
      <VolumeLayer
        trackId={clip.trackId}
        clipId={clip.id}
        width={width - 2}
        sideBeingResized={
          isResizingLeft ? 'left' : isResizingRight ? 'right' : undefined
        }
      />
      <div
        className="handle absolute -left-2 top-0 z-50 mt-11 hidden h-10 min-w-2 cursor-col-resize flex-col items-center justify-center gap-[3px] rounded-l-[4px] group-hover:flex"
        style={
          isResizingLeft || isResizingRight || isSelected
            ? { display: 'flex' }
            : {}
        }
        onMouseDown={handleMouseDownLeft}
      >
        <div className="h-[3px] w-[3px] rounded-full bg-white" />
        <div className="h-[3px] w-[3px] rounded-full bg-white" />
        <div className="h-[3px] w-[3px] rounded-full bg-white" />
      </div>
      <div
        className="handle absolute -right-2 top-0 z-50 mt-11 hidden h-10 min-w-2 cursor-col-resize flex-col items-center justify-center gap-[3px] rounded-r-[4px] group-hover:flex"
        style={
          isResizingLeft || isResizingRight || isSelected
            ? { display: 'flex' }
            : {}
        }
        onMouseDown={handleMouseDownRight}
      >
        <div className="h-[3px] w-[3px] rounded-full bg-white" />
        <div className="h-[3px] w-[3px] rounded-full bg-white" />
        <div className="h-[3px] w-[3px] rounded-full bg-white" />
      </div>
      <div
        className="corner-info pointer-events-none absolute left-0 top-0 inline-flex h-5 overflow-hidden rounded-br-md rounded-tl-sm"
        style={{ maxWidth: width - 2 }}
      >
        <div className="flex items-center justify-between gap-1 px-2 py-0.5 text-xs font-semibold">
          {clip.type === TrackType.Voices && isVoiceClip(clip) ? (
            <Icon name="addNote" width={12} />
          ) : (
            <button
              type="button"
              data-ignore-click
              className={`pointer-events-auto rounded p-0.5 transition-colors ${
                isGainControlOpen
                  ? 'bg-green-600 text-white'
                  : 'text-inherit hover:bg-white/10'
              }`}
              onClick={toggleGainControl}
              aria-expanded={isGainControlOpen}
              aria-label="Track gain"
            >
              <Icon name="volumeHigh" width={12} />
            </button>
          )}
          <span className="whitespace-nowrap capitalize">{clip.name}</span>
        </div>
      </div>
      {audioBuffer && (
        <div className="corner-info absolute bottom-0 right-0 flex h-5 w-5 items-center justify-center gap-1 rounded-br-sm rounded-tl-md">
          <button type="button" onClick={onDownloadClip} data-ignore-click>
            <Icon name="download" width={15} height={15} />
          </button>
        </div>
      )}
    </div>
  )
}

export default memo(ClipContainer)
